import React, { useState } from 'react';
import { FieldWrapper } from '@progress/kendo-react-form';
import { DateTimePicker } from '@progress/kendo-react-dateinputs';
import { FloatingLabel, Error, Hint } from '@progress/kendo-react-labels';
import toastr from 'shared/helpers/toastr';
import BaseDateTimeInput from '../inputs/datePickers/BaseDateTimeInput';
import CustomDateTimeInput from '../inputs/datePickers/CustomDateTimeInput';
// eslint-disable-next-line no-unused-vars
import BaseFormControl from './BaseFormControl';

/**
 * A datetime picker form field.
 *
 * @typedef {React.ComponentProps<BaseFormControl>} BaseFieldRenderProps
 * @typedef {React.PropsWithoutRef<DateTimePicker>} TelerikDateTimePickerProps
 * @typedef {React.ComponentPropsWithoutRef<CustomDateTimeInput>} CustomDateTimeInputProps
 *
 * @param {BaseFieldRenderProps & TelerikDateTimePickerProps & CustomDateTimeInputProps} fieldRenderProps
 * @returns {React.ReactElement} Datetime picker form field.
 */
const FormDateTimePicker = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    name,
    valid,
    disabled,
    hint,
    wrapperStyle,
    value,
    stateChangesAfterOnChange,
    wrapperClassName,
    optional,
    timeZone,
    ...others
  } = fieldRenderProps;

  const editorId = id ?? name;

  const showValidationMessage = touched && validationMessage;
  const showHint = !showValidationMessage && hint;
  const hintId = showHint ? `${editorId}_hint` : '';
  const errorId = showValidationMessage ? `${editorId}_error` : '';
  const labelId = label ? `${editorId}_label` : '';
  const mappedValue = typeof value === 'string' ? new Date(value) : value;
  const [stateValue, setStateValue] = useState();
  const [warningCss, setWarningCss] = useState('');

  const handleOnChange = (e) => {
    const mimicEvent = { target: { value: e.value } };
    others.onChange(mimicEvent);
  };

  if (others.minimal) {
    const minimalComponentStyle = {
      backgroundColor: '',
      width: '8rem',
      zoom: '.8',
      ...[others.style ? { ...others.style } : {}],
    };

    return (
      <CustomDateTimeInput
        validationMessage={validationMessage}
        touched={touched}
        label={label}
        id={editorId}
        name={name}
        valid={valid}
        disabled={disabled}
        hint={hint}
        wrapperStyle={wrapperStyle}
        value={mappedValue}
        style={minimalComponentStyle}
        {...others}
        onChange={handleOnChange}
        timeZone={timeZone}
      />
    );
  }

  const doWeNeedaWarning = (d) => {
    if (d) {
      const then = new Date(d);
      const now = new Date().getDate();
      if (others.maxDaysWarning) {
        const max = new Date().setDate(now + others.maxDaysWarning);
        if (then > max) {
          return 'future';
        }
      }
      if (others.minDaysWarning) {
        const min = new Date().setDate(now - others.minDaysWarning);
        if (then < min) {
          return 'past';
        }
      }
    }
    return '';
  };

  const updateOnEachChange = () => {
    if (value === stateValue) {
      return;
    }
    const warning = doWeNeedaWarning(value);
    if (warning === 'future') {
      return 'max-days-warning';
    } if (warning === 'past') {
      return 'max-days-warning';
    }
  };

  /**
     * @description -
     * Handles the onChange event of the date picker if datePicker has minDaysWarning or maxDaysWarning property set
     * @param {Object} e - The event object
     */
  const changed = async (e) => {
    setStateValue(e.target.value);
    setWarningCss('');
    const l = doWeNeedaWarning(e.target.value);
    if (l === 'future') {
      setWarningCss('max-days-warning from-component');
      toastr('warning', `The date you entered is more than ${others.maxDaysWarning} days in the future. Please check your date for typos`);
    } else if (l === 'past') {
      setWarningCss('max-days-warning from-component');
      toastr('warning', `The date you entered is more than ${others.minDaysWarning} days in the past. Please check your date for typos`);
    }

    others.onChange(e);
  };

  if (others.maxDaysWarning || others.minDaysWarning) {
    return (
      <FieldWrapper style={wrapperStyle} className={wrapperClassName}>
        <FloatingLabel
          label={label}
          id={labelId}
          optional={optional}
          editorId={editorId}
          editorValid={valid}
          editorDisabled={disabled}
          editorValue={value}
        >
          <DateTimePicker
            {...others}
            onChange={changed}
            ariaLabelledBy={labelId}
            ariaDescribedBy={`${hintId} ${errorId}`}
            valid={valid}
            id={editorId}
            name={name}
            dateInput={props => <CustomDateTimeInput {...props} timeZone={timeZone} />}
            disabled={disabled}
            value={mappedValue}
            className={stateChangesAfterOnChange ? updateOnEachChange() : warningCss}
            timeZone={timeZone}
          />
        </FloatingLabel>
        {showHint && <Hint id={hintId}>{hint}</Hint>}
        {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
      </FieldWrapper>
    );
  }

  if (!label) {
    return (
      <FieldWrapper style={wrapperStyle}>
        <DateTimePicker
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          valid={valid}
          id={editorId}
          name={name}
          disabled={disabled}
          value={mappedValue}
          dateInput={BaseDateTimeInput}
          {...others}
        />
        {showHint && <Hint id={hintId}>{hint}</Hint>}
        {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
      </FieldWrapper>
    );
  }

  return (
    <FieldWrapper style={wrapperStyle}>
      <FloatingLabel
        label={label}
        id={labelId}
        optional={optional}
        editorId={editorId}
        editorValid={valid}
        editorDisabled={disabled}
        editorValue={value}
      >
        <DateTimePicker
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          valid={valid}
          id={editorId}
          name={name}
          disabled={disabled}
          value={mappedValue}
          dateInput={props => <BaseDateTimeInput {...props} timeZone={timeZone} />}
          timeZone={timeZone}
          {...others}
        />
      </FloatingLabel>
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  );
};

export default FormDateTimePicker;
